import React from "react";
import { MapPinned, Phone, TriangleAlert } from "lucide-react";
import { StatusBadge } from "./StatusBadge";

const NEXT_ACTION = {
  assigned: "en_route",
  accepted: "en_route",
  en_route: "arrived",
  arrived: "in_progress",
  in_progress: "completed",
  issue_reported: "completed",
};

function actionLabel(status) {
  const next = NEXT_ACTION[status] || null;
  if (next === "en_route") return "Mark en route";
  if (next === "arrived") return "Mark arrived";
  if (next === "in_progress") return "Start service";
  if (next === "completed") return "Complete job";
  return null;
}

export function DriverActiveJob({ job, events = [], busy, onStatusAction, onComplete, onReportIssue }) {
  if (!job) return null;
  const nextStatus = NEXT_ACTION[job.status] || null;
  return (
    <div className="space-y-4" data-testid={`driver-active-job-${job.id}`}>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">{job.booking_reference_snapshot || "Driver job"}</p>
            <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-slate-900">{job.service_type_snapshot?.replace(/_/g, " ") || "Assigned job"}</h1>
            <p className="mt-1 text-sm text-slate-500">{new Date(job.planned_start_time).toLocaleString()}</p>
          </div>
          <StatusBadge status={job.status} testId={`driver-active-status-${job.id}`} />
        </div>

        <div className="mt-5 space-y-3 text-sm text-slate-700">
          <p><span className="font-semibold text-slate-900">Customer:</span> {job.customer_name_snapshot || "—"}</p>
          <p><span className="font-semibold text-slate-900">Phone:</span> {job.customer_phone_snapshot || "—"}</p>
          <p><span className="font-semibold text-slate-900">Pickup:</span> {job.pickup_location || "—"}</p>
          <p><span className="font-semibold text-slate-900">Destination:</span> {job.destination || "—"}</p>
          {job.passenger_count_snapshot ? <p><span className="font-semibold text-slate-900">Passengers:</span> {job.passenger_count_snapshot}</p> : null}
          <p><span className="font-semibold text-slate-900">Vehicle:</span> {job.assigned_vehicle_name || job.assigned_vehicle_id || "Vehicle pending"}</p>
          {job.notes_snapshot ? <p><span className="font-semibold text-slate-900">Notes:</span> {job.notes_snapshot}</p> : null}
          {job.admin_notes_snapshot ? <p><span className="font-semibold text-slate-900">Admin notes:</span> {job.admin_notes_snapshot}</p> : null}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          {job.customer_phone_snapshot && <a href={`tel:${job.customer_phone_snapshot}`} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700" data-testid="driver-detail-call"><Phone size={15} /> Call customer</a>}
          {job.pickup_location && <a href={`https://maps.google.com/?q=${encodeURIComponent(job.pickup_location)}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700" data-testid="driver-detail-maps"><MapPinned size={15} /> Open maps</a>}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">Task timeline</h2>
        <div className="mt-4 space-y-3">
          {events.length === 0 ? <p className="text-sm text-slate-500">No updates yet.</p> : events.map((event) => (
            <div key={event.id} className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">{event.new_status?.replace(/_/g, " ") || event.event_type}</p>
              <p className="mt-1 text-xs text-slate-500">{new Date(event.created_at).toLocaleString()}</p>
              {event.note ? <p className="mt-1 text-xs text-slate-600">{event.note}</p> : null}
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {nextStatus && nextStatus !== "completed" && (
          <button type="button" disabled={busy} onClick={() => onStatusAction?.(nextStatus)} data-testid={`driver-next-status-${nextStatus}`} className="w-full rounded-2xl bg-slate-900 px-5 py-4 text-sm font-semibold text-white disabled:opacity-50">
            {actionLabel(job.status)}
          </button>
        )}
        {(nextStatus === "completed" || job.status === "issue_reported") && (
          <button type="button" disabled={busy} onClick={() => onComplete?.()} data-testid="driver-complete-job" className="w-full rounded-2xl bg-emerald-600 px-5 py-4 text-sm font-semibold text-white disabled:opacity-50">
            Complete job
          </button>
        )}
        <button type="button" onClick={() => onReportIssue?.()} data-testid="driver-report-issue" className="w-full rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4 text-sm font-semibold text-amber-900">
          <span className="inline-flex items-center gap-2"><TriangleAlert size={16} /> Report issue</span>
        </button>
      </div>
    </div>
  );
}